import type { FormControlType } from '@common/types/form-element.type';
import type {
    FormBaseInputFormType,
    FormCheckboxFormType,
    FormElementFormType,
    FormFileFormType,
    FormInputDateFormType,
    FormInputFormType,
    FormInputHiddenFormType,
    FormInputNumberFormType,
    FormRadioFormType,
    FormSelectFormType,
    FormTextareaFormType,
    OptionFormGroup,
    OptionFormType,
} from '@common/types/form-element-forms.type';
import type {
    PaymentElementFormGroup,
    PaymentElementFormType,
    RroElementFormType,
} from '@common/types/payment-element.form';
import { type RroInfoElementFormGroup } from '@common/types/payment-element.form';

import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { EFormControlType, EFormInputType } from '@common/enums';
import { UtilityValidator } from '@common/validators/utility.validator';
import { type TranslateService } from '@ngx-translate/core';

const formBaseInputFormType = (): FormBaseInputFormType => ({
    label: new FormControl(null),
    variable: new FormControl(null),
    crmContactVariable: new FormControl(null, [Validators.required]),
    crmDealVariable: new FormControl(null, [Validators.required]),
    telegramVariable: new FormControl(null, [Validators.required]),
    instagramVariable: new FormControl(null, [Validators.required]),
    viberVariable: new FormControl(null, [Validators.required]),
    messengerVariable: new FormControl(null, [Validators.required]),
    whatsappVariable: new FormControl(null, [Validators.required]),
    tiktokVariable: new FormControl(null, [Validators.required]),
    required: new FormControl(null),
    protected: new FormControl(null),
    isDynamicPlaceholderEnabled: new FormControl(null),
    value: new FormControl(null, [Validators.required]),
    crmCommentPlace: new FormControl(null, [Validators.required]),
});

export const formElementFormInputFormGroup = (): FormInputFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    placeholder: new FormControl(null),
    htmlInputType: new FormControl(null),
    autocompleteType: new FormControl(null),
});

export const formElementFormTextareaFormGroup = (): FormTextareaFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    placeholder: new FormControl(null),
    autocompleteType: new FormControl(null),
});
export const formElementFormFileFormGroup = (): FormFileFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    placeholder: new FormControl(null),
    htmlInputType: new FormControl(null),
});

export const formElementFormInputDateFormGroup = (): FormInputDateFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    htmlInputType: new FormControl(null),
    autocompleteType: new FormControl(null),
});

export const formElementFormInputHiddenFormGroup = (): FormInputHiddenFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    htmlInputType: new FormControl(null),
    autocompleteType: new FormControl(null),
});

export const formElementFormCheckboxFormGroup = (): FormCheckboxFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    htmlInputType: new FormControl(null),
});

export const formElementFormRadioFormGroup = (): FormRadioFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    htmlInputType: new FormControl(null),
    options: new FormArray<OptionFormGroup>([]),
});

export const formElementFormSelectFormGroup = (): FormSelectFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    htmlInputType: new FormControl(null),
    options: new FormArray<OptionFormGroup>([]),
});

export const formElementOptionFormGroup = (): FormGroup<OptionFormType> =>
    new FormGroup<OptionFormType>({
        value: new FormControl(null),
        label: new FormControl(null),
        selected: new FormControl(null),
    });

export const rroPaymentElementFormGroup = (): RroInfoElementFormGroup =>
    new FormGroup<RroElementFormType>({
        id: new FormControl(null),
        cost: new FormControl(null),
        amount: new FormControl(1),
        price: new FormControl(null),
    });

export const formElementFormInputNumberFormGroup = (): FormInputNumberFormType => ({
    ...formBaseInputFormType(),
    type: new FormControl(null),
    htmlInputType: new FormControl(null),
    autocompleteType: new FormControl(null),
    placeholder: new FormControl(null),
});

export const formElementForm = (type: FormControlType): FormGroup<FormElementFormType> => {
    const formElementMap: Partial<Record<FormControlType, () => FormElementFormType>> = {
        [EFormInputType.input]: formElementFormInputFormGroup,
        [EFormInputType.name]: formElementFormInputFormGroup,
        [EFormInputType.email]: formElementFormInputFormGroup,
        [EFormInputType.phone]: formElementFormInputFormGroup,
        [EFormInputType.address]: formElementFormInputFormGroup,
        [EFormInputType.url]: formElementFormInputFormGroup,
        [EFormInputType.date]: formElementFormInputDateFormGroup,
        [EFormControlType.select]: formElementFormSelectFormGroup,
        [EFormControlType.radio]: formElementFormRadioFormGroup,
        [EFormControlType.checkbox]: formElementFormCheckboxFormGroup,
        [EFormInputType.hidden]: formElementFormInputHiddenFormGroup,
        [EFormControlType.textarea]: formElementFormTextareaFormGroup,
        [EFormInputType.password]: formElementFormInputFormGroup,
        [EFormInputType.file]: formElementFormFileFormGroup,
        [EFormInputType.number]: formElementFormInputNumberFormGroup,
    };

    const validators = [];

    if (type !== EFormControlType.textarea) {
        validators.push(
            UtilityValidator.atLeastOne<FormElementFormType>(Validators.required, [
                'crmContactVariable',
                'crmDealVariable',
            ])
        );
    }

    // element type may be missing or unknown in stored widget data — fall back to the generic input group
    // so the settings panel still builds a control at the same index instead of throwing
    const formGroupFactory = formElementMap[type] ?? formElementFormInputFormGroup;

    return new FormGroup<FormElementFormType>(formGroupFactory(), { validators });
};

export const paymentElementFormGroup = (translate?: TranslateService): PaymentElementFormGroup =>
    new FormGroup<PaymentElementFormType>({
        type: new FormControl(null),
        amount: new FormControl(null, [Validators.required, Validators.min(0.01)]),
        currency: new FormControl(null),
        payment_method_id: new FormControl(null, [Validators.required]),
        isEditableTotalCost: new FormControl(null),
        isActive: new FormControl(null),
        customPaymentName: new FormControl(
            translate ? translate.instant('custom_payment_label') : 'custom_payment_label',
            [Validators.required]
        ),
        newPaymentName: new FormControl(null),
        rro_isActive: new FormControl(null),
        rro_info: new FormGroup<RroElementFormType>({
            id: new FormControl(null),
            cost: new FormControl(null),
            amount: new FormControl(1),
            price: new FormControl(null),
        }),
        eduPaymentMethodId: new FormControl(null),
    });
